import { ISchemaList } from 'data/schema-list';
import { UiServices } from 'data/service-spec';
import { getContentType } from "data/metadata"
import { IFileWithMeta, IUploadParams } from 'react-dropzone-uploader';
import { Path, quoteTrim, pathJoin } from 'utility';
import { IDirectorySpec } from './directory-spec';
import { config } from 'config';
import { User } from './user';
import { RsMime } from './mime-type';

const serverAcceptHeader = 'X-RestSpace-Server-Accept';

export interface Api {
    fetchIndex(): Promise<any>;
    fetchItem(itemPath: string): Promise<[object, any]>;
    fetchText(itemPath: string): Promise<[string, string]>;
    fetchHead(itemPath: string): Promise<IReqHeaders>;
    saveItem(path: string, item: any, mimeType?: string): Promise<string>;
    saveIndex(index: ISchemaList[]): Promise<boolean>;
    deleteItem(itemPath: string): Promise<number>;
    moveFile(schema: object, oldItemPath: string, newItemPath: string): Promise<boolean>;
    uploaderParams(file: IFileWithMeta, schema: object, itemPath: string): Promise<IUploadParams>;
    fileUrl(schema: object, itemPath: string): string;
    login(email: string, password: string): Promise<User>;
    loggedInUser(): Promise<object>;
    logout(): Promise<boolean>;
}

export interface IReqHeaders {
    mimeType: string;
    serviceName: string;
}

export class FileServiceApi implements Api {
    commonInit: RequestInit = {
        credentials: "include",
        headers: {
            'X-Restspace-Request-Mode': 'editor' 
        }
    };

    constructor(public apiUrl: string) {}

    async fetchIndex() {
        const resp = await fetch(pathJoin(this.apiUrl, "json/schema/_index"), this.commonInit);
        const val: ISchemaList[] = await resp.json();
        console.log(val);
        return val;
    }

    async fetchServices() {
        const resp = await fetch(pathJoin(this.apiUrl, "services.json/ui"), this.commonInit);
        const serviceSpecs: UiServices = await resp.json();
        return serviceSpecs;
    }

    async fetchList(path: string, uid: number): Promise<IDirectorySpec> {
        const resp = await fetch(pathJoin(this.apiUrl, path, '/?uid=' + uid.toString()), this.commonInit);
        const dirSpec: IDirectorySpec = resp.status == 404 ? { files: [] } : await resp.json();
        return dirSpec;
    }

    async fetchSchema(schemaUrl: string) {
        const url = schemaUrl.startsWith('/') ? pathJoin(this.apiUrl, schemaUrl) : schemaUrl;
        const resp = await fetch(url, this.commonInit);
        return await resp.json();
    }

    async fetchGet(path: string): Promise<[string, IReqHeaders]> {
        const resp = await fetch(pathJoin(this.apiUrl, path), this.commonInit);
        const reqHeaders = {
            mimeType: resp.headers.get('Content-Type') || '',
            serviceName: resp.headers.get('X-Restspace-Service') || ''
        }
        let text = '';
        if (new RsMime().isText(reqHeaders.mimeType)) text = await resp.text();
        return [text, reqHeaders];
    }

    async fetchItem(path: string): Promise<[object, any]> {
        const resp = await fetch(pathJoin(this.apiUrl, path), this.commonInit);
        if (resp.status === 404) {
            return [{}, ''];
        } else {
            const data = await resp.json();
            const respType = resp.headers.get('Content-Type');
            if (respType === null || respType.indexOf('schema=') < 0)
                throw "response lacks a content type with a schema";
            const schemaUrl = quoteTrim(respType.split('schema=')[1]);
            const schema = await this.fetchSchema(schemaUrl);
            return [schema, data];
        }
    }

    async fetchText(path: string): Promise<[string, string]> {
        const resp = await fetch(pathJoin(this.apiUrl, path), this.commonInit);
        if (resp.status === 404) {
            return ['', ''];
        } else {
            const text = await resp.text();
            const respType = resp.headers.get('Content-Type');
            return [text, respType || ''];
        }
    }

    async fetchHead(path: string): Promise<IReqHeaders> {
        const resp = await fetch(pathJoin(this.apiUrl, path), { ...this.commonInit, method: 'HEAD' });
        if (resp.status === 404) {
            return { mimeType: '', serviceName: '' };
        } else {
            const reqHeaders = {
                mimeType: resp.headers.get('Content-Type') || '',
                serviceName: resp.headers.get('X-Restspace-Service') || ''
            }
            return reqHeaders;
        }
    }

    async saveItem(path: string, item: any, mimeType: string) {
        const body = (typeof item === 'string' ? item : JSON.stringify(item));
        const ext = new Path(path).extension;
        if (!ext) path += '.' + config.mime.getExtension(mimeType);
        const resp = await fetch(pathJoin(this.apiUrl, path),
            {
                ...this.commonInit,
                method: 'PUT', mode: 'cors', body,
                headers: {
                    'Content-Type': mimeType,
                    'X-Restspace-Request-Mode': 'editor'
                }
            });
        return resp.ok ? '' : await resp.text();
    }
    
    async saveIndex(index: ISchemaList[]) {
        const resp = await fetch(pathJoin(this.apiUrl, `json/schema/_index`),
        {
            ...this.commonInit,
            method: 'PUT', mode: 'cors', body: JSON.stringify(index),
            headers: {
                'Content-Type': 'application/json',
                'X-Restspace-Request-Mode': 'editor'
            }
        });
        return resp.ok;
    }

    async deleteItem(itemPath: string) {
        const resp = await fetch(pathJoin(this.apiUrl, itemPath),
            {
                ...this.commonInit,
                method: 'DELETE', mode: 'cors'
            });
        return resp.ok ? 0 : resp.status;
    }

    async moveFile(schema: object, oldItemPath: string, newItemPath: string) {
        const resp = await fetch(pathJoin(this.apiUrl, `file/${oldItemPath}`),
            {
                ...this.commonInit,
                method: 'POST', body: JSON.stringify({ destination: newItemPath }),
                headers: {
                    'Content-Type': 'application/json',
                    'X-Restspace-Request-Mode': 'editor'
                }
            });
        return resp.ok;
    }

    async uploaderParams(fileWithMeta: IFileWithMeta, schema: object, itemPath: string) {
        const contentType = getContentType(schema);
        const fileNamePath = new Path(fileWithMeta.meta.name);
        return {
            url: pathJoin(this.apiUrl, `file/${contentType}/${itemPath}.${fileNamePath.extension}`),
            body: fileWithMeta.file,

        };
    }

    fileUrl(schema: object, itemPath: string) {
        const contentType = getContentType(schema);
        return pathJoin(this.apiUrl, `file/${contentType}/${itemPath}`);
    }

    urlFromPath(path: string) {
        return pathJoin(this.apiUrl, path);
    }

    async login(email: string, password: string): Promise<User> {
        const resp = await fetch(pathJoin(this.apiUrl, 'auth/login'),
        {
            ...this.commonInit,
            method: 'POST', mode: 'cors', body: JSON.stringify({ email, password }),
            headers: {
                'Content-Type': 'application/json',
                'X-Restspace-Request-Mode': 'editor'
            }
        });
        if (resp.status === 401 || resp.status === 404)
            return User.AnonUser();
        
        const user = await resp.json();
        return new User(user);
    }

    async logout() {
        const resp = await fetch(pathJoin(this.apiUrl, 'auth/logout'),
        {
            ...this.commonInit,
            method: 'POST', mode: 'cors'
        });
        return resp.ok;
    }

    async loggedInUser(): Promise<User> {
        try {
            const resp = await fetch(pathJoin(this.apiUrl, 'auth/user'), this.commonInit);
            const user = (await resp.json()) as object;
            return new User(user);
        } catch(err) {
            console.log('loggedInUser err: ' + JSON.stringify(err));
            return User.AnonUser();
        }
    }
}